import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ArrowLeft, ArrowRight, Rocket, CheckCircle, AlertCircle, Wrench } from 'lucide-react';
import apiClient from '../api/client';
import FrameworkPicker, { type CompatibilityInfo } from './FrameworkPicker';
import DeployProgress from './DeployProgress';

interface ProfileSummary {
  version: string;
  status: string;
  skillCount: number;
  workflowCount: number;
  toolCount: number;
  memoryCount: number;
  llmModel: string;
  personaConfig?: Record<string, unknown> | null;
}

/** B15 preflight 清单结构（与后端 GET /agents/{id}/deploy-preflight 对齐）。 */
interface PreflightFixAction {
  kind: string;
  ref?: string | null;
  label?: string;
}

interface PreflightCheck {
  key: string;
  label: string;
  status: 'ok' | 'missing' | 'broken';
  detail: string;
  suggestion: string;
  fix_action: PreflightFixAction | null;
}

interface PreflightReport {
  agent_id: string;
  framework: string | null;
  checks: PreflightCheck[];
  summary: { ok: number; missing: number; broken: number; total: number; blocking: boolean };
}

interface PreflightFixResult {
  kind: string;
  status: 'fixed' | 'skipped' | 'manual' | 'failed';
  detail: string;
  ref?: string | null;
}

interface DeployWizardProps {
  agentId: string;
  profileId: string;
  profileSummary: ProfileSummary;
  onDeploy: () => void;
  onCancel: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 200,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  width: '90%',
  maxWidth: 640,
  maxHeight: '90vh',
  overflow: 'auto',
  padding: 28,
};

function checkCompatibility(
  framework: string,
  summary: ProfileSummary
): CompatibilityInfo {
  const issues: string[] = [];
  switch (framework) {
    case 'celestial_toad':
      if (!summary.personaConfig) issues.push('CT 需要人设配置');
      break;
    case 'hermes': {
      const samples = summary.personaConfig?.samples;
      if (!Array.isArray(samples) || samples.length === 0) {
        issues.push('Hermes 需要 persona samples');
      }
      break;
    }
    case 'openclaw':
      if (summary.toolCount === 0) issues.push('OpenClaw 需要至少一个挂载工具');
      break;
  }
  return { compatible: issues.length === 0, issues };
}

// 从 axios 错误中提取后端 detail 文案
function apiErrorDetail(e: unknown): string | null {
  const err = e as { response?: { data?: { detail?: unknown } } };
  return typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
}

export default function DeployWizard({ agentId, profileId, profileSummary, onDeploy, onCancel }: DeployWizardProps) {
  const [step, setStep] = useState(1);
  const [selectedFramework, setSelectedFramework] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [autoRollback, setAutoRollback] = useState(false);

  // Deployment tracking（P11 事件驱动：进度由 DeployProgress 订阅任务事件流推导）
  const [deploymentStatus, setDeploymentStatus] = useState<string>('');
  const [deploymentError, setDeploymentError] = useState<string | null>(null);
  const [deployJobId, setDeployJobId] = useState<string>('');
  const [deploying, setDeploying] = useState(false);

  // B15 preflight 能力装配：清单 + 一键修复（失败诚实降级，不阻塞向导原流程）
  const [preflight, setPreflight] = useState<PreflightReport | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [fixing, setFixing] = useState(false);
  const [fixResults, setFixResults] = useState<PreflightFixResult[] | null>(null);
  const preflightReqRef = useRef(0);
  // 记录上一次选中的框架，用于在渲染期重置 preflight 相关状态（见下方 adjust-during-render）
  const [prevSelectedFramework, setPrevSelectedFramework] = useState(selectedFramework);

  // 进行中的部署任务持久化到 sessionStorage：页面刷新后重开向导可从事件流恢复进度
  const jobStorageKey = `atelier:deploy-job:${agentId}:${profileId}`;

  // Compute compatibility for all frameworks
  const compatibility: Record<string, CompatibilityInfo> = {
    celestial_toad: checkCompatibility('celestial_toad', profileSummary),
    hermes: checkCompatibility('hermes', profileSummary),
    openclaw: checkCompatibility('openclaw', profileSummary),
    openhuman: { compatible: true, issues: [] },
    generic: { compatible: true, issues: [] },
  };

  const selectedCompat = selectedFramework ? compatibility[selectedFramework] : null;

  // 框架切换时在渲染期重置 preflight 相关状态（React 推荐的 adjust-state-during-render），
  // 避免在 effect 体内同步 setState 造成级联渲染（react-hooks/set-state-in-effect）
  if (prevSelectedFramework !== selectedFramework) {
    setPrevSelectedFramework(selectedFramework);
    setPreflight(null);
    setFixResults(null);
    setPreflightError(null);
  }

  // 清单加载中由「已选框架 + 尚无清单/错误」推导，无需额外维护 loading state
  const preflightLoading = selectedFramework !== '' && preflight === null && preflightError === null;

  // 选择框架后拉取 preflight 清单（B15）；请求竞态用自增 id 丢弃过期响应
  useEffect(() => {
    if (!selectedFramework) return;
    const reqId = preflightReqRef.current + 1;
    preflightReqRef.current = reqId;
    apiClient
      .get(`/agents/${agentId}/deploy-preflight?framework=${encodeURIComponent(selectedFramework)}`)
      .then((res) => {
        if (preflightReqRef.current !== reqId) return; // 已有更新的请求
        setPreflight(res.data as PreflightReport);
      })
      .catch((e: unknown) => {
        if (preflightReqRef.current !== reqId) return;
        // 诚实降级：preflight 失败不阻塞向导原流程，仅提示
        const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
        setPreflightError(detail ? String(detail) : (e as Error)?.message || 'preflight 检查失败');
        setPreflight(null);
      });
    // agentId / selectedFramework 变化时重新检查
  }, [selectedFramework, agentId]);

  // B15 一键修复：把缺失/损坏项的 fix_action 提交给后端执行，成功后重拉清单
  const handlePreflightFix = async () => {
    if (!preflight || fixing) return;
    const actions = preflight.checks
      .filter((c) => c.status !== 'ok' && c.fix_action)
      .map((c) => ({ kind: c.fix_action!.kind, ref: c.fix_action!.ref ?? undefined }));
    if (actions.length === 0) return;
    setFixing(true);
    setFixResults(null);
    try {
      const res = await apiClient.post(`/agents/${agentId}/deploy-preflight/fix`, { actions });
      setFixResults((res.data?.results as PreflightFixResult[]) ?? []);
      // 重新拉取清单，反映修复后的状态
      const fresh = await apiClient.get(
        `/agents/${agentId}/deploy-preflight?framework=${encodeURIComponent(selectedFramework || '')}`
      );
      setPreflight(fresh.data as PreflightReport);
    } catch (e) {
      setFixResults([
        {
          kind: 'unknown',
          status: 'failed',
          detail: (e as Error)?.message || '一键修复失败',
        },
      ]);
    } finally {
      setFixing(false);
    }
  };

  // 挂载时恢复进行中的部署任务（页面刷新/重开向导）：DeployProgress 会从历史事件重建进度
  useEffect(() => {
    // 通过本地包装触发恢复，避免在 effect 体内直接同步调用 setState（react-hooks/set-state-in-effect）
    const tick = () => {
      const savedJobId = sessionStorage.getItem(jobStorageKey);
      if (savedJobId) {
        setDeployJobId(savedJobId);
        setDeploymentStatus('pending');
        setDeploying(true);
        setStep(2);
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeploy = async () => {
    if (!selectedFramework) return;
    setDeploying(true);
    setDeploymentStatus('pending');
    setDeploymentError(null);
    try {
      const res = await apiClient.post('/deployments', {
        agent_id: agentId,
        profile_id: profileId,
        framework: selectedFramework,
        config: {
          dry_run: dryRun,
          auto_rollback: autoRollback,
        },
      });
      const data = res.data;
      const jobId = data.job_id as string | undefined;
      if (!jobId) {
        throw new Error('后端未返回部署任务 ID，无法订阅部署进度');
      }
      // 部署在请求内已执行完毕，进度视图通过事件流重放/续传呈现
      setDeployJobId(jobId);
      sessionStorage.setItem(jobStorageKey, jobId);
    } catch (e) {
      setDeploymentStatus('failed');
      setDeploymentError(apiErrorDetail(e) || (e as Error)?.message || '部署请求失败');
      setDeploying(false);
    }
  };

  // 部署任务到达终态（由 DeployProgress 从 done 事件推导）
  const handleSettled = useCallback(
    (status: 'success' | 'failed', error?: string | null) => {
      setDeploymentStatus(status);
      setDeploymentError(error ?? null);
      setDeploying(false);
      sessionStorage.removeItem(jobStorageKey);
    },
    [jobStorageKey]
  );

  const handleRetry = () => {
    setDeploymentStatus('');
    setDeploymentError(null);
    setDeployJobId('');
    sessionStorage.removeItem(jobStorageKey);
  };

  const isFinished = deploymentStatus === 'success' || deploymentStatus === 'failed';

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ margin: 0 }}>
            <Rocket size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            部署向导
          </h3>
          <button className="secondary" style={{ padding: '6px 8px' }} onClick={onCancel}>
            <X size={16} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div
            className="flex items-center justify-center"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: step >= 1 ? 'var(--color-primary)' : 'var(--bg-tertiary)',
              color: 'white',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            1
          </div>
          <span style={{ fontSize: 13, color: step >= 1 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            选择框架
          </span>
          <div style={{ flex: 1, height: 1, background: 'var(--border-default)' }} />
          <div
            className="flex items-center justify-center"
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: step >= 2 ? 'var(--color-primary)' : 'var(--bg-tertiary)',
              color: 'white',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            2
          </div>
          <span style={{ fontSize: 13, color: step >= 2 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            确认部署
          </span>
        </div>

        {/* Step 1: Framework Selection */}
        {step === 1 && (
          <div className="flex flex-col gap-4">
            <p className="text-muted" style={{ fontSize: 13 }}>
              选择目标框架，系统将检查当前 Profile 与该框架的兼容性。
            </p>
            <FrameworkPicker
              selected={selectedFramework}
              onSelect={setSelectedFramework}
              compatibility={compatibility}
            />

            {/* B15 preflight 能力装配清单：选择框架后展示缺口与一键修复（失败诚实降级） */}
            {selectedFramework && (
              <div className="card" style={{ background: 'var(--bg-primary)', padding: 14 }}>
                <div className="flex items-center justify-between mb-2">
                  <span style={{ fontSize: 13, fontWeight: 600 }}>部署前检查（preflight）</span>
                  {preflight && preflight.summary.blocking && (
                    <span className="badge warning" style={{ fontSize: 11 }}>
                      {preflight.summary.missing + preflight.summary.broken} 项缺口
                    </span>
                  )}
                  {preflight && !preflight.summary.blocking && (
                    <span className="badge success" style={{ fontSize: 11 }}>
                      就绪
                    </span>
                  )}
                </div>

                {preflightLoading && (
                  <p className="text-muted" style={{ fontSize: 12, margin: '6px 0' }}>
                    检查中...
                  </p>
                )}

                {preflightError && (
                  <p style={{ fontSize: 12, margin: '6px 0', color: 'var(--color-warning)' }}>
                    无法获取 preflight 清单（{preflightError}），可跳过继续部署。
                  </p>
                )}

                {preflight && !preflightLoading && (
                  <>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {preflight.checks.map((check) => (
                        <li
                          key={check.key}
                          className="flex flex-col"
                          style={{
                            gap: 2,
                            padding: '6px 0',
                            borderBottom: '1px solid var(--border-default)',
                            fontSize: 12,
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {check.status === 'ok' ? (
                              <CheckCircle size={13} style={{ color: 'var(--color-success)' }} />
                            ) : check.status === 'missing' ? (
                              <AlertCircle size={13} style={{ color: 'var(--color-warning)' }} />
                            ) : (
                              <AlertCircle size={13} style={{ color: 'var(--color-error)' }} />
                            )}
                            <span style={{ fontWeight: 500 }}>{check.label}</span>
                            <span
                              className="text-muted"
                              style={{
                                fontSize: 11,
                                color:
                                  check.status === 'ok'
                                    ? 'var(--color-success)'
                                    : check.status === 'missing'
                                      ? 'var(--color-warning)'
                                      : 'var(--color-error)',
                              }}
                            >
                              {check.status === 'ok' ? '正常' : check.status === 'missing' ? '缺失' : '异常'}
                            </span>
                          </div>
                          <p style={{ margin: 0, paddingLeft: 21, color: 'var(--text-muted)' }}>{check.detail}</p>
                        </li>
                      ))}
                    </ul>

                    {preflight.summary.blocking && (
                      <button
                        className="secondary"
                        style={{ marginTop: 10, fontSize: 12 }}
                        onClick={handlePreflightFix}
                        disabled={fixing}
                      >
                        <Wrench size={13} /> {fixing ? '修复中...' : '一键修复'}
                      </button>
                    )}

                    {fixResults && fixResults.length > 0 && (
                      <div className="flex flex-col gap-1 mt-2" style={{ fontSize: 12 }}>
                        {fixResults.map((r, i) => (
                          <p
                            key={i}
                            style={{
                              margin: 0,
                              color:
                                r.status === 'fixed'
                                  ? 'var(--color-success)'
                                  : r.status === 'manual'
                                    ? 'var(--color-warning)'
                                    : r.status === 'skipped'
                                      ? 'var(--text-muted)'
                                      : 'var(--color-error)',
                            }}
                          >
                            [{r.status === 'fixed' ? '已修复' : r.status === 'manual' ? '需人工' : r.status === 'skipped' ? '已就绪' : '失败'}]{' '}
                            {r.detail}
                          </p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {selectedCompat && !selectedCompat.compatible && (
              <div
                className="card"
                style={{
                  borderColor: 'var(--color-warning)',
                  background: 'rgba(240,136,62,0.1)',
                  padding: 12,
                }}
              >
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} style={{ color: 'var(--color-warning)' }} />
                  <span style={{ color: 'var(--color-warning)', fontSize: 13, fontWeight: 500 }}>
                    兼容性警告
                  </span>
                </div>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 12, color: 'var(--color-warning)' }}>
                  {selectedCompat.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end mt-2">
              <button
                className="primary"
                onClick={() => setStep(2)}
                disabled={!selectedFramework || (selectedCompat != null && !selectedCompat.compatible)}
              >
                下一步 <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Summary & Deploy */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            {/* Profile Summary */}
            <div className="card" style={{ background: 'var(--bg-primary)', padding: 16 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>配置摘要</h4>
              <div className="grid grid-cols-2 gap-3" style={{ fontSize: 13 }}>
                <div>
                  <span className="text-muted">Profile 版本:</span>{' '}
                  <strong>{profileSummary.version}</strong>
                  <span className={`badge ${profileSummary.status === 'active' ? 'success' : 'info'}`} style={{ marginLeft: 8 }}>
                    {profileSummary.status}
                  </span>
                </div>
                <div>
                  <span className="text-muted">LLM:</span>{' '}
                  <strong>{profileSummary.llmModel}</strong>
                </div>
                <div>
                  <span className="text-muted">技能:</span>{' '}
                  <strong>{profileSummary.skillCount}</strong>
                </div>
                <div>
                  <span className="text-muted">工作流:</span>{' '}
                  <strong>{profileSummary.workflowCount}</strong>
                </div>
                <div>
                  <span className="text-muted">工具:</span>{' '}
                  <strong>{profileSummary.toolCount}</strong>
                </div>
                <div>
                  <span className="text-muted">记忆:</span>{' '}
                  <strong>{profileSummary.memoryCount}</strong>
                </div>
              </div>
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-default)' }}>
                <span className="text-muted">目标框架:</span>{' '}
                <strong>{selectedFramework}</strong>
              </div>
            </div>

            {/* Options */}
            {!deploying && !isFinished && (
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2" style={{ cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  <span className="text-muted">部署前检查 (dry_run)</span>
                </label>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={autoRollback}
                    onChange={(e) => setAutoRollback(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  <span className="text-muted">失败自动回滚</span>
                </label>
              </div>
            )}

            {/* Progress（事件驱动：先拉历史事件重建，再接 SSE 实时流） */}
            {(deploying || isFinished) && deployJobId && (
              <DeployProgress
                jobId={deployJobId}
                onSettled={handleSettled}
                onRetry={isFinished && deploymentStatus === 'failed' ? handleRetry : undefined}
              />
            )}
            {/* 提交阶段即失败（未拿到 job_id）时的错误展示 */}
            {isFinished && !deployJobId && deploymentError && (
              <div
                className="card"
                style={{
                  background: 'rgba(248,81,73,0.1)',
                  border: '1px solid var(--color-error)',
                  padding: 12,
                }}
              >
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} style={{ color: 'var(--color-error)' }} />
                  <span style={{ color: 'var(--color-error)', fontSize: 13, fontWeight: 500 }}>部署失败</span>
                </div>
                <p style={{ color: 'var(--color-error)', fontSize: 12, margin: '8px 0 0' }}>{deploymentError}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between mt-2">
              <button
                className="secondary"
                onClick={() => {
                  if (isFinished) {
                    handleRetry();
                  }
                  setStep(1);
                }}
                disabled={deploying && !isFinished}
              >
                <ArrowLeft size={14} /> {isFinished ? '重新选择' : '上一步'}
              </button>

              {!isFinished ? (
                <button
                  className="primary"
                  onClick={handleDeploy}
                  disabled={deploying}
                >
                  {deploying ? (
                    <>部署中...</>
                  ) : (
                    <><Rocket size={14} /> 确认部署</>
                  )}
                </button>
              ) : deploymentStatus === 'success' ? (
                <button className="primary" onClick={onDeploy}>
                  <CheckCircle size={14} /> 查看部署
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
